import Api from '../Api'

export interface IUrlShortener {
  id: number
  title: string
  original_url: string
  short_path: string
  utm_campaign?: string
  utm_source?: string
  utm_medium?: string
  utm_content?: string
  utm_term?: string
  access_count: number
  period_clicks: number
  qr_code_url: string
  user: {
    id: number
    name: string
  }
  created_at: string
}

export interface ICreateUrlShortenerPayload {
  title: string
  original_url: string
  short_path?: string
  utm_campaign?: string
  utm_source?: string
  utm_medium?: string
  utm_content?: string
  utm_term?: string
  used_by: 'marketing' | 'commercial'
}

export interface IListUtms {
  utm_campaign?: string[]
  utm_source?: string[]
  utm_medium?: string[]
  utm_content?: string[]
  utm_term?: string[]
}

export interface IListResponse {
  data: IUrlShortener[]
  recordsTotal: number
  recordsFiltered: number
}

export interface IListUtmsResponse {
  data: IListUtms
}

export interface IUtm {
  id: number
  value: string
  utm: 'campaign' | 'source' | 'medium' | 'content' | 'term'
  type: 'commercial' | 'marketing'
  usage_count: number
  created_at: string
}

export interface IListUtmsManagementResponse {
  data: IUtm[]
  recordsTotal: number
  recordsFiltered: number
}

export interface IGetUrlShortenerResponse {
  id: number
  user_id: number
  title: string
  original_url: string
  short_path: string
  utm_campaign?: string
  utm_source?: string
  utm_medium?: string
  utm_content?: string
  utm_term?: string
  used_by: 'marketing' | 'commercial'
  access_count: number
  created_at: string
  deleted_at: string
  clicks: IGetUrlShortenerClicks[]
}

export interface IGetUrlShortenerClicks {
  id: number
  url_shortener_id: number
  ip_address: string
  user_agent: string
  referrer: string
  country: string
  device_type: string
  browser: string
  os: string
  clicked_at: string
  created_at: string
  updated_at: string
}

const UrlShortenerService = {
  async list(
    used_by: string,
    search?: string,
    page?: number,
    pageSize?: number,
    from?: string,
    to?: string
  ): Promise<IListResponse> {
    const params = {
      search: search || '',
      page: page || 1, // Default to page 1 if not provided
      page_size: pageSize || 10, // Default to 10 items per page if not provided,
      from: from || '',
      to: to || '',
      used_by: used_by
    }
    const { data } = await Api.get('/url-shorteners', { params })
    return data
  },

  async listUtms(type: 'marketing' | 'commercial'): Promise<IListUtmsResponse> {
    const { data } = await Api.get('/url-shorteners/utms', {
      params: { type }
    })
    return data
  },

  async deleteUtm(utm: string, utmType: keyof IListUtms, type: 'marketing' | 'commercial'): Promise<void> {
    await Api.delete(`/url-shorteners/utm/destroyByName`, {
      params: { utm, utmType, type }
    })
  },

  async get(id: number): Promise<IGetUrlShortenerResponse> {
    const { data } = await Api.get(`/url-shorteners/${id}`)
    return data
  },

  async create(payload: ICreateUrlShortenerPayload): Promise<IUrlShortener> {
    const { data } = await Api.post('/url-shorteners', payload)
    return data
  },

  async delete(id: number): Promise<void> {
    await Api.delete(`/url-shorteners/${id}`)
  },

  async update(id: number, payload: ICreateUrlShortenerPayload): Promise<IUrlShortener> {
    const { data } = await Api.put(`/url-shorteners/${id}`, payload)
    return data
  },

  async listUtmsManagement(
    type: 'commercial' | 'marketing',
    utmFilter?: string,
    search?: string,
    page?: number,
    pageSize?: number
  ): Promise<IListUtmsManagementResponse> {
    const { data } = await Api.get('/url-shorteners/utm-management', {
      params: {
        type,
        utm: utmFilter || '',
        search: search || '',
        page: page || 1,
        page_size: pageSize || 25
      }
    })
    return data
  },

  async createUtm(value: string, utm: IUtm['utm'], type: 'commercial' | 'marketing'): Promise<IUtm> {
    const { data } = await Api.post('/url-shorteners/utm-management', { value, utm, type })
    return data
  },

  // Troca o valor também nas URLs curtas do mesmo tipo que usavam o antigo.
  async updateUtm(id: number, value: string): Promise<IUtm & { affected_urls: number }> {
    const { data } = await Api.put(`/url-shorteners/utm-management/${id}`, { value })
    return data
  },

  async deleteUtmById(id: number): Promise<void> {
    await Api.delete(`/url-shorteners/utm-management/${id}`)
  }
}

export default UrlShortenerService
